import { useEffect, useState } from "react";
import { Bell, BellDot, Check, CheckCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { api, formatDateTimeBR } from "@/lib/api";

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch {}
  };

  const markOneRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      load();
    } catch {}
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-slate-700" data-testid="notification-bell">
          {unread > 0 ? <BellDot className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
          {unread > 0 && (
            <span className="absolute top-1 right-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-600 text-white text-[10px] font-semibold">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="font-semibold text-sm">Notificações</div>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-midnight hover:underline">
              <CheckCheck className="inline w-3.5 h-3.5 mr-1" /> Marcar todas como lidas
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto scroll-thin">
          {items.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Sem notificações.</div>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                className={`px-4 py-3 border-b border-slate-100 ${n.read ? "" : "bg-blue-50/50"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">{n.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{n.message}</div>
                    <div className="text-[11px] text-slate-500 mt-1">{formatDateTimeBR(n.created_at)}</div>
                    {n.link && (
                      <Link
                        to={n.link}
                        onClick={() => { markOneRead(n.id); setOpen(false); }}
                        className="text-xs text-midnight hover:underline mt-1 inline-block"
                      >
                        Acessar →
                      </Link>
                    )}
                  </div>
                  {!n.read && (
                    <button onClick={() => markOneRead(n.id)} title="Marcar como lida">
                      <Check className="w-4 h-4 text-slate-400 hover:text-emerald-600" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
